import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Bot, Send, X } from 'lucide-react';

type Message = {
  id: number;
  from: 'bot' | 'user';
  text: string;
};

const QUICK_REPLIES = [
  'What services do you offer?',
  'How much does it cost?',
  'How do I schedule?',
  'Where are you located?',
  'Tell me about your team',
];

const RESPONSES: { keywords: string[]; reply: string }[] = [
  {
    keywords: ['service', 'offer', 'clean'],
    reply:
      "We'd love to help! 😊 We offer Residential Cleaning, Commercial & Office Cleaning, and Specialty Services like carpet cleaning, power washing, and disinfection — whatever your space needs, we're happy to take care of it.",
  },
  {
    keywords: ['cost', 'price', 'pricing', 'much', 'quote', 'estimate'],
    reply:
      "Happy to share! 💛 Our gentle starting rates are: Apartments $75/hr (min $150), Townhouses $85/hr (min $170), Single-family homes $90/hr (min $180), and Commercial spaces $90–$95/hr. If you'd like an exact quote, just request a free estimate and we'll take great care of you.",
  },
  {
    keywords: ['schedule', 'book', 'appointment', 'when'],
    reply:
      "We'd be delighted to get you on the calendar! You can choose Weekly, Bi-Weekly, or Monthly cleanings, or something custom made just for you. Simply fill out our \"Request a Service\" form and our team will gently follow up to confirm a time.",
  },
  {
    keywords: ['located', 'location', 'address', 'where', 'area'],
    reply:
      "We're located at 481 N Frederick Ave, Suite 412, Gaithersburg, MD 20877, and we warmly serve Montgomery County and the surrounding areas, including Germantown, MD. 🏡",
  },
  {
    keywords: ['team', 'staff', 'who', 'employee'],
    reply:
      "Our team means the world to us! They're caring, detail-focused cleaning professionals who follow our signature \"Detail-Clean Approach\" to make sure every visit feels like a fresh start.",
  },
  {
    keywords: ['phone', 'call', 'contact', 'number'],
    reply:
      "Of course! You're welcome to reach us anytime at 240-750-9535 during business hours, or by email at info@axiscleaning.com — we're always glad to hear from you. 😊",
  },
  {
    keywords: ['email', 'mail'],
    reply: "You can gently reach us at info@axiscleaning.com and we'll get back to you within 24 hours. 💌",
  },
  {
    keywords: ['hi', 'hello', 'hey'],
    reply: "Hi there! 👋 So glad you're here — how can I help make your space feel fresh today?",
  },
];

function getBotReply(userText: string): string {
  const lower = userText.toLowerCase();
  const match = RESPONSES.find((r) => r.keywords.some((k) => lower.includes(k)));
  if (match) return match.reply;
  return "I'm so sorry, I'm not quite able to answer that one right now 🙏 — please feel free to call us at 240-750-9535 and our team will be happy to help.";
}

export default function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    { id: 1, from: 'bot', text: "Hi there! I'm the AXIS Cleaning Assistant. 👋 So happy you're here — how can I gently help you today?" },
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, isTyping, isOpen]);

  const sendMessage = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;

    const userMsg: Message = { id: Date.now(), from: 'user', text: trimmed };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setIsTyping(true);

    setTimeout(() => {
      const botMsg: Message = { id: Date.now() + 1, from: 'bot', text: getBotReply(trimmed) };
      setMessages((prev) => [...prev, botMsg]);
      setIsTyping(false);
    }, 700);
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="mb-4 w-[26rem] max-w-[calc(100vw-3rem)] h-[32rem] max-h-[calc(100vh-8rem)] bg-white rounded-3xl shadow-2xl shadow-axis-950/20 border border-sand-100 flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="clean-gradient px-5 py-4 flex items-center gap-3 flex-shrink-0">
              <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-white font-display font-semibold text-base leading-tight truncate">
                  AXIS Assistant
                </h3>
                <p className="text-white/80 text-xs flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-300 inline-block" />
                  Online now
                </p>
              </div>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-sand-50/40">
              {messages.map((msg) => (
                <div key={msg.id} className={`flex ${msg.from === 'user' ? 'justify-end' : 'justify-start'} gap-2`}>
                  {msg.from === 'bot' && (
                    <div className="w-7 h-7 rounded-full bg-axis-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Bot className="w-3.5 h-3.5 text-axis-600" />
                    </div>
                  )}
                  <div
                    className={`max-w-[75%] px-4 py-2.5 text-sm leading-relaxed rounded-2xl ${
                      msg.from === 'user'
                        ? 'bg-axis-600 text-white rounded-br-sm'
                        : 'bg-white text-gray-700 shadow-sm border border-sand-100 rounded-bl-sm'
                    }`}
                  >
                    {msg.text}
                  </div>
                </div>
              ))}

              {isTyping && (
                <div className="flex justify-start gap-2">
                  <div className="w-7 h-7 rounded-full bg-axis-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Bot className="w-3.5 h-3.5 text-axis-600" />
                  </div>
                  <div className="bg-white border border-sand-100 shadow-sm rounded-2xl rounded-bl-sm px-4 py-3 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-axis-300 animate-bounce [animation-delay:-0.3s]" />
                    <span className="w-1.5 h-1.5 rounded-full bg-axis-300 animate-bounce [animation-delay:-0.15s]" />
                    <span className="w-1.5 h-1.5 rounded-full bg-axis-300 animate-bounce" />
                  </div>
                </div>
              )}

              {!isTyping && (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {QUICK_REPLIES.map((reply) => (
                    <button
                      key={reply}
                      onClick={() => sendMessage(reply)}
                      className="whitespace-nowrap text-[10px] font-medium text-axis-700 bg-axis-50 hover:bg-axis-100 border border-axis-100 rounded-full px-2 py-1 transition-colors"
                    >
                      {reply}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Input */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                sendMessage(input);
              }}
              className="flex-shrink-0 border-t border-sand-100 bg-white px-3 py-3 flex items-center gap-2"
            >
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your message..."
                className="flex-1 px-4 py-2.5 bg-sand-50 border border-sand-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-axis-500/30 focus:border-axis-500 transition-all"
              />
              <button
                type="submit"
                disabled={!input.trim()}
                aria-label="Send message"
                className="w-10 h-10 rounded-full clean-gradient text-white flex items-center justify-center flex-shrink-0 shadow-md shadow-axis-500/20 disabled:opacity-40 disabled:cursor-not-allowed hover:scale-105 transition-transform"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toggle Button */}
      <motion.button
        onClick={() => setIsOpen((v) => !v)}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        aria-label={isOpen ? 'Close chat' : 'Open chat'}
        className="w-16 h-16 rounded-full clean-gradient shadow-xl shadow-axis-500/30 flex items-center justify-center relative"
      >
        <AnimatePresence mode="wait" initial={false}>
          {isOpen ? (
            <motion.span
              key="close"
              initial={{ opacity: 0, rotate: -90 }}
              animate={{ opacity: 1, rotate: 0 }}
              exit={{ opacity: 0, rotate: 90 }}
              transition={{ duration: 0.2 }}
            >
              <X className="w-6 h-6 text-white" />
            </motion.span>
          ) : (
            <motion.span
              key="bot"
              initial={{ opacity: 0, rotate: 90 }}
              animate={{ opacity: 1, rotate: 0 }}
              exit={{ opacity: 0, rotate: -90 }}
              transition={{ duration: 0.2 }}
            >
              <Bot className="w-7 h-7 text-white" />
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>
    </div>
  );
}
